import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * WebSocket connection status
 */
type WebSocketStatus = 'idle' | 'connecting' | 'open' | 'closing' | 'closed';

/**
 * Options for WebSocket connection management
 */
interface WebSocketOptions {
  /** Maximum number of reconnection attempts (default: 5) */
  maxReconnectAttempts?: number;
  /** Initial delay in ms before first reconnection attempt (default: 1000) */
  reconnectInitialDelay?: number;
  /** Exponential backoff factor for subsequent reconnect attempts (default: 2) */
  reconnectBackoffFactor?: number;
}

const DEFAULT_OPTIONS: Required<WebSocketOptions> = {
  maxReconnectAttempts: 5,
  reconnectInitialDelay: 1000, // 1 second
  reconnectBackoffFactor: 2,
};

/**
 * Return type for useWebSocketManager hook
 */
interface UseWebSocketManagerReturn {
  /** Current connection status */
  connectionStatus: WebSocketStatus;
  /** Function to send messages over the WebSocket */
  sendMessage: (message: object) => boolean;
  /** Most recent error during connection or message handling */
  lastError: Error | null;
}

/**
 * Hook for managing a WebSocket connection with automatic reconnection
 *
 * @param getUrl Function that returns the WebSocket URL to connect to (return null to prevent connection)
 * @param onMessageReceived Callback function to handle received messages
 * @param options Connection options
 */
export const useWebSocketManager = (
  getUrl: () => string | null,
  onMessageReceived: (data: any) => void,
  options?: WebSocketOptions
): UseWebSocketManagerReturn => {
  const { maxReconnectAttempts, reconnectInitialDelay, reconnectBackoffFactor } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  // WebSocket instance and related refs
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isIntentionalCloseRef = useRef<boolean>(false);
  const outgoingQueueRef = useRef<string[]>([]);
  const onMessageReceivedRef = useRef(onMessageReceived);

  // State exposed to consumers
  const [connectionStatus, setConnectionStatus] = useState<WebSocketStatus>('idle');
  const [lastError, setLastError] = useState<Error | null>(null);

  // Keep callback reference up to date
  useEffect(() => {
    onMessageReceivedRef.current = onMessageReceived;
  }, [onMessageReceived]);

  /**
   * Handles successful WebSocket connection
   */
  const handleOpen = useCallback(() => {
    console.log('WebSocket connected');
    setConnectionStatus('open');
    setLastError(null);
    reconnectAttemptsRef.current = 0; // Reset attempts on successful connection

    // Process any queued messages
    const queue = [...outgoingQueueRef.current]; // Create a copy to avoid processing new additions
    outgoingQueueRef.current = [];

    if (queue.length > 0) {
      console.log(`Processing ${queue.length} queued messages`);
      queue.forEach((msg) => {
        try {
          websocketRef.current?.send(msg);
        } catch (err) {
          console.error('Error sending queued message:', err);
          // Only requeue if the socket is still open
          if (websocketRef.current?.readyState === WebSocket.OPEN) {
            outgoingQueueRef.current.push(msg);
          }
        }
      });
    }
  }, []);

  /**
   * Handles WebSocket closure
   */
  const handleClose = useCallback(
    (event: CloseEvent) => {
      console.log(
        `WebSocket closed: code=${event.code}, reason=${event.reason}, wasClean=${event.wasClean}`
      );

      // Update state first, then handle reconnection logic
      setConnectionStatus('closed');

      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Determine if we should attempt to reconnect
      // Don't reconnect if:
      // 1. Close was intentional (user initiated)
      // 2. Close code indicates a normal closure (1000)
      // 3. We've exceeded maximum reconnect attempts
      const isNormalClosure = event.code === 1000;

      if (!isIntentionalCloseRef.current && !isNormalClosure) {
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const attempt = reconnectAttemptsRef.current + 1;
          const delay = reconnectInitialDelay * Math.pow(reconnectBackoffFactor, attempt - 1);

          console.log(
            `Attempting WebSocket reconnect ${attempt}/${maxReconnectAttempts} in ${delay}ms...`
          );

          // Set status to connecting to indicate reconnection in progress
          setConnectionStatus('connecting');

          // Schedule reconnection attempt
          reconnectTimeoutRef.current = setTimeout(() => {
            // Reset websocketRef before attempting to reconnect
            websocketRef.current = null;

            connect(); // Will be defined below
            reconnectTimeoutRef.current = null;
          }, delay);

          reconnectAttemptsRef.current = attempt;
        } else {
          console.error(`WebSocket max reconnect attempts (${maxReconnectAttempts}) reached.`);
          setLastError(
            new Error(`Maximum reconnection attempts reached (${maxReconnectAttempts})`)
          );
          // Don't reset intentional flag here - if we hit max attempts, any further connection
          // should be treated as a new connection attempt
        }
      } else {
        // Reset intentional flag for any future reconnections
        isIntentionalCloseRef.current = false;
      }
    },
    [maxReconnectAttempts, reconnectInitialDelay, reconnectBackoffFactor]
  );

  /**
   * Handles WebSocket errors
   */
  const handleError = useCallback((event: Event) => {
    console.error('WebSocket error:', event);
    // Store error with a more descriptive message
    const errorMsg = event instanceof ErrorEvent ? event.message : 'Unknown WebSocket error';
    setLastError(new Error(errorMsg));
    // Don't set status here, as close event will follow
  }, []);

  /**
   * Handles incoming WebSocket messages
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      onMessageReceivedRef.current(data);
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error, event.data);
      setLastError(error instanceof Error ? error : new Error('Invalid message format'));
    }
  }, []);

  /**
   * Initiates a WebSocket connection
   */
  const connect = useCallback(() => {
    // Get the current URL
    const currentUrl = getUrl();

    // Don't connect if URL is missing or if we already have an active connection
    if (!currentUrl) {
      console.log('WebSocket connect skipped: No URL provided');
      return;
    }

    // Check existing connection
    if (websocketRef.current) {
      const readyState = websocketRef.current.readyState;
      if (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING) {
        console.log('WebSocket connect skipped: Already connecting/connected', {
          readyState,
          status: connectionStatus,
        });
        return;
      }

      // Clean up existing connection if it's closing/closed
      websocketRef.current = null;
    }

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    console.log('Initiating WebSocket connection to:', currentUrl);
    setConnectionStatus('connecting');
    setLastError(null);

    try {
      const ws = new WebSocket(currentUrl);
      websocketRef.current = ws;

      // Assign event handlers
      ws.onopen = handleOpen;
      ws.onclose = handleClose;
      ws.onerror = handleError;
      ws.onmessage = handleMessage;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setLastError(error instanceof Error ? error : new Error('Failed to create WebSocket'));
      setConnectionStatus('closed');
    }
  }, [getUrl, handleOpen, handleClose, handleError, handleMessage]);

  /**
   * Intentionally closes the WebSocket connection
   */
  const disconnect = useCallback(() => {
    console.log('Initiating intentional WebSocket disconnect');

    // Mark this as an intentional close to prevent auto-reconnect
    isIntentionalCloseRef.current = true;

    // Clear any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Only attempt to close if we have an active connection
    if (websocketRef.current) {
      // Set status to closing before initiating close
      setConnectionStatus('closing');

      try {
        // Clean closure with code 1000 (normal)
        websocketRef.current.close(1000, 'Client disconnecting');
        // handleClose will set status to closed and reset websocketRef
      } catch (error) {
        console.error('Error during WebSocket close:', error);
        // Force status update and cleanup on error
        setConnectionStatus('closed');
        websocketRef.current = null;
      }
    } else {
      // No active connection to close
      setConnectionStatus('closed');
    }

    // Reset reconnect attempts when intentionally disconnecting
    reconnectAttemptsRef.current = 0;
  }, []);

  /**
   * Sends a message over the WebSocket
   * @returns true if message was sent or queued, false if it failed
   */
  const sendMessage = useCallback((message: object): boolean => {
    const messageString = JSON.stringify(message);

    // Check connection state using the actual WebSocket readyState for reliability
    const isConnected = websocketRef.current?.readyState === WebSocket.OPEN;

    if (isConnected) {
      try {
        websocketRef.current?.send(messageString);
        return true;
      } catch (error) {
        console.error('WebSocket send error:', error);
        setLastError(error instanceof Error ? error : new Error('Failed to send message'));
        return false;
      }
    } else {
      // Queue message if connection is not open
      console.log('WebSocket not open, queuing message');
      outgoingQueueRef.current.push(messageString);
      return true; // Message was queued successfully
    }
  }, []);

  // Main effect to manage connection lifecycle
  useEffect(() => {
    const currentUrl = getUrl();
    if (currentUrl) {
      connect();
    } else if (connectionStatus !== 'idle' && connectionStatus !== 'closed') {
      // Disconnect if URL becomes null and we're not already idle/closed
      disconnect();
    }

    // Cleanup on unmount or URL change
    return () => {
      if (websocketRef.current) {
        disconnect();
      }
    };
  }, [getUrl, connect, disconnect]);

  return { connectionStatus, sendMessage, lastError };
};
